import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable, shareReplay, tap } from 'rxjs';
import { environment } from '../../../../../../environments/environment';
import { OrganisationRow, UnassignedOrganisationParty } from '../models/organisations';
import { OrganisationDetail, PutOrganisationRequest } from '../models/organisation-detail';
import { AssignableParty } from '../../contacts/components/add-department-contact/models/assignable-party';

@Injectable({
  providedIn: 'root'
})
export class OrganisationsService {
  private http = inject(HttpClient);

  // getUnassigned()'s response runs ~3.1MB — fine once rendered (virtual scroll), but slow to
  // *download* on a poor connection, and it used to only start once the admin opened the "Add
  // organisation" popover. Caching it here lets OrganisationsListComponent kick the request off
  // eagerly, in parallel with getAssigned(), so by the time the popover actually opens the
  // response is already in flight or done. shareReplay(1) multicasts that one request to every
  // subscriber (the list's eager prefetch, then the popover); refCount: false keeps the cached
  // value alive after both unsubscribe, so a later popover open reuses it instead of refetching.
  private unassignedCache$: Observable<AssignableParty[]> | null = null;

  // Fetches every organisation with at least one assigned contact, unpaged — the list now holds
  // this in memory and filters/sorts client-side (search/sort/virtual-scroll all run against this
  // one array), the same shape ContactsService.getAllContacts() uses. See
  // contacts/VIRTUAL_SCROLL.md for why.
  getAssigned(): Observable<OrganisationRow[]> {
    return this.http.get<OrganisationRow[]>(`${environment.apiUrl}/supportContacts/organisations/assigned`);
  }

  // Lightweight total-count-only endpoint — for badges that only need the number, not rows.
  count(): Observable<number> {
    return this.http.get<number>(`${environment.apiUrl}/supportContacts/organisations/count`);
  }

  // For the "Add organisation" popover — every CRM party with no organisation assignment yet,
  // unpaged, then filtered client-side (the same fetch-once + local-filter pattern getAssigned()
  // above and AddPersonContactComponent's eligible-users fetch use). Cached across calls — see
  // unassignedCache$ above — so OrganisationsListComponent can prefetch it and the popover just
  // reuses that. A failed request clears the cache (via the tap's error handler) so the *next*
  // getUnassigned() call — the next prefetch, or the popover reopening — retries instead of
  // replaying the same error forever.
  getUnassigned(): Observable<AssignableParty[]> {
    if (!this.unassignedCache$) {
      this.unassignedCache$ = this.http
        .get<UnassignedOrganisationParty[]>(`${environment.apiUrl}/supportContacts/organisations/unassigned`)
        .pipe(
          map(parties =>
            parties.map(party => ({ id: party.partyId, name: party.partyName, number: party.partyNumber }))
          ),
          tap({ error: () => (this.unassignedCache$ = null) }),
          shareReplay({ bufferSize: 1, refCount: false })
        );
    }
    return this.unassignedCache$;
  }

  // Called by OrganisationsListComponent on every grid load (initial load and reload() alike, see
  // its prefetchUnassigned()) so a cached list can never outlive the assigned/unassigned split it
  // was fetched against — e.g. a party the grid just (re)shows as assigned must stop being
  // offered from the popover as still-unassigned. The next getUnassigned() call — the list's own
  // re-prefetch, or a later popover open — fetches a fresh copy.
  invalidateUnassignedCache(): void {
    this.unassignedCache$ = null;
  }

  getDetail(partyNumber: string): Observable<OrganisationDetail> {
    return this.http.get<OrganisationDetail>(`${environment.apiUrl}/supportContacts/organisations/${partyNumber}`);
  }

  // Atomic full replace of the organisation's assignment set — see PutOrganisationRequest.
  // Unlike getDetail() above, this PUT is keyed by partyId, not partyNumber.
  save(partyId: number, request: PutOrganisationRequest): Observable<OrganisationDetail> {
    return this.http.put<OrganisationDetail>(`${environment.apiUrl}/supportContacts/organisations/${partyId}`, request);
  }
}
